import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Building2, Edit2, Users, Plus, X, Upload, ExternalLink, LayoutGrid, Clock, CheckSquare, Square, ChevronRight } from "lucide-react";
import { Brand } from "../../components/Brand";
import { useAuth } from "../../lib/auth";
import { useInicio } from "../../lib/permisos";

interface Sucursal {
  id: number;
  codigo: string;
  nombre: string;
  ciudad?: string;
  provincia?: string;
  pais?: string;
  direccion?: string;
  telefono?: string;
  email?: string;
  horarioApertura?: string;
  capacidadPersonas?: number;
  activa: boolean;
  reservable?: boolean;
  whatsappNumero?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  tripadvisorUrl?: string;
  websiteUrl?: string;
  politicaConfirmacionHoras?: number;
  usaDuracionReserva?: boolean;
  deliveryActivo?: boolean;
  deliveryHoraInicio?: string | null;
  deliveryHoraFin?: string | null;
}

interface HorarioDia { inicio: string; fin: string; nextDay?: boolean; }
interface HorarioSemanal { [dia: string]: HorarioDia | null; }

interface ZonaConfig {
  id: number;
  nombre: string;
  orden: number;
  permiteSeleccionMesa?: boolean;
  bloquesTiempoMinutos?: number | null;
  politicaConfirmacionHoras?: number | null;
  sinVencimiento?: boolean;
  maxDiasAnticipacion?: number | null;
  maxReservasPeriodo?: number | null;
  periodoDias?: number | null;
  horarioSemanal?: HorarioSemanal | null;
  mesasMaxPorHora?: number | null;
  descripcion?: string | null;
  // ── Pago opcional para reservar (zonas por hora) ──
  requierePagoReserva?: boolean;
  montoReservaUsd?: number | null;
  articuloId?: number | null;
  instruccionesPago?: string | null;
  mensajePoliticaReserva?: string | null;
  horasLimitePago?: number | null;
  toleranciaLlegadaMinutos?: number | null;
  aceptaWalkin?: boolean;
  visibleWeb?: boolean;
  nombreWeb?: string | null;
}

const DIAS = [
  { key: '1', label: 'Lun' }, { key: '2', label: 'Mar' },
  { key: '3', label: 'Mié' }, { key: '4', label: 'Jue' },
  { key: '5', label: 'Vie' }, { key: '6', label: 'Sáb' },
  { key: '0', label: 'Dom' },
];

function HorarioSemanalEditor({ zonaId, horario, onChange }: {
  zonaId: number;
  horario: HorarioSemanal | null | undefined;
  onChange: (h: HorarioSemanal) => void;
}) {
  const h: HorarioSemanal = horario ?? {};

  function setDia(dia: string, patch: Partial<HorarioDia> | null) {
    const next = { ...h };
    if (patch === null) {
      next[dia] = null;
    } else {
      next[dia] = { inicio: '14:00', fin: '23:00', ...h[dia], ...patch };
    }
    onChange(next);
  }

  function copiarPrimeroATodos() {
    const ref = DIAS.find(d => h[d.key] !== null && h[d.key] !== undefined);
    if (!ref) return;
    const base = h[ref.key]!;
    const next: HorarioSemanal = {};
    for (const d of DIAS) { next[d.key] = { ...base }; }
    onChange(next);
  }

  return (
    <div className="border-t border-ink-100 mt-2 pt-2">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide">Horario por día</span>
        <button onClick={copiarPrimeroATodos}
          className="text-[10px] text-brand-600 hover:text-brand-800 font-medium">
          ↓ Copiar a todos
        </button>
      </div>
      <div className="space-y-1">
        {DIAS.map(({ key, label }) => {
          const dia = h[key];
          const abierto = dia !== null && dia !== undefined;
          return (
            <div key={key} className="grid grid-cols-12 gap-1 items-center">
              <div className="col-span-1 text-[10px] font-medium text-ink-600">{label}</div>
              <div className="col-span-1">
                <button
                  onClick={() => setDia(key, abierto ? null : { inicio: '14:00', fin: '23:00' })}
                  className={`w-5 h-5 rounded text-[9px] font-bold transition-colors ${abierto ? 'bg-emerald-500 text-white' : 'bg-ink-200 text-ink-400'}`}
                  title={abierto ? 'Abierto — clic para cerrar' : 'Cerrado — clic para abrir'}
                >
                  {abierto ? '✓' : '—'}
                </button>
              </div>
              {abierto ? (
                <>
                  <div className="col-span-3 flex items-center gap-0.5">
                    <input type="time" value={dia!.inicio}
                      onChange={e => setDia(key, { inicio: e.target.value })}
                      className="text-[10px] border border-ink-200 rounded px-1 py-0.5 w-full" />
                  </div>
                  <div className="col-span-1 text-[9px] text-ink-400 text-center">→</div>
                  <div className="col-span-3 flex items-center gap-0.5">
                    <input type="time" value={dia!.fin}
                      onChange={e => setDia(key, { fin: e.target.value })}
                      className="text-[10px] border border-ink-200 rounded px-1 py-0.5 w-full" />
                  </div>
                  <div className="col-span-3 flex items-center gap-1">
                    <input type="checkbox" id={`nd-${zonaId}-${key}`}
                      checked={dia!.nextDay ?? false}
                      onChange={e => setDia(key, { nextDay: e.target.checked })}
                      className="w-3 h-3 accent-brand-600" />
                    <label htmlFor={`nd-${zonaId}-${key}`}
                      className="text-[9px] text-ink-500 cursor-pointer">+1 día</label>
                  </div>
                </>
              ) : (
                <div className="col-span-10 text-[10px] text-ink-300 italic">Cerrado</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface RecursoConfig {
  id: number;
  nombre: string;
  capacidad: number;
  tipoNombre?: string;
  reservableOnline: boolean;
  horaInicio?: string | null;
  horaFin?: string | null;
  visibleWeb?: boolean;
  nombreWeb?: string | null;
}

interface UserSucursal {
  id: string;
  email: string;
  fullName: string;
  role: string;
}


interface MenuConfig {
  logoUrl?: string | null;
  imagenFondoUrl?: string | null;
  colorPrimario?: string;
  colorSecundario?: string;
  mostrarLlamarMesonero?: boolean;
  mostrarSolicitarCuenta?: boolean;
}

export default function SucursalesAdmin() {
  const inicio = useInicio();
  const { token } = useAuth();
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [allUsers, setAllUsers] = useState<UserSucursal[]>([]);
  const [selected, setSelected] = useState<Sucursal | null>(null);
  const [menuCfg, setMenuCfg] = useState<MenuConfig>({});
  const [sucUsers, setSucUsers] = useState<UserSucursal[]>([]);
  const [zonasCfg, setZonasCfg] = useState<ZonaConfig[]>([]);
  const [savingZonas, setSavingZonas] = useState(false);
  const [recursosZona, setRecursosZona] = useState<Record<number, RecursoConfig[]>>({});
  const [zonaExpandida, setZonaExpandida] = useState<number | null>(null);
  const [zonaHorarioAbierto, setZonaHorarioAbierto] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [moduloPagosActivo, setModuloPagosActivo] = useState<boolean>(false);
  const [orgBranding, setOrgBranding] = useState<{ logoUrl: string | null; colorPrimario: string | null }>({ logoUrl: null, colorPrimario: null });
  const [savingOrgBranding, setSavingOrgBranding] = useState(false);
  const [savedOrgBranding, setSavedOrgBranding] = useState(false);
  const [uploadingOrgLogo, setUploadingOrgLogo] = useState(false);
  const logoRef = useRef<HTMLInputElement>(null);
  const bgRef = useRef<HTMLInputElement>(null);
  const orgLogoRef = useRef<HTMLInputElement>(null);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const cargarSucursales = () => {
    fetch("/api/v1/sucursales/admin/all", { headers }).then(r => r.json()).then(setSucursales).catch(() => {});
  };

  const cargarUsuarios = () => {
    fetch("/api/v1/users", { headers })
      .then(r => r.ok ? r.json() : [])
      .then((d: any) => setAllUsers(Array.isArray(d) ? d : (Array.isArray(d?.items) ? d.items : [])))
      .catch(() => setAllUsers([]));
  };

  useEffect(() => {
    cargarSucursales();
    cargarUsuarios();
    // Estado del módulo de pagos de reservas
    fetch("/api/v1/admin/pagos-reservas/modulo", { headers })
      .then(r => r.ok ? r.json() : { activo: false })
      .then((d: any) => setModuloPagosActivo(!!d?.activo))
      .catch(() => setModuloPagosActivo(false));
    // Identidad del login (grupo/organización)
    fetch("/api/v1/sucursales/org-branding", { headers })
      .then(r => r.ok ? r.json() : { logoUrl: null, colorPrimario: null })
      .then(setOrgBranding)
      .catch(() => {});
  }, [token]);

  const guardarOrgBranding = async () => {
    setSavingOrgBranding(true);
    try {
      await fetch("/api/v1/sucursales/org-branding", {
        method: "PUT", headers,
        body: JSON.stringify(orgBranding),
      });
      setSavedOrgBranding(true);
      setTimeout(() => setSavedOrgBranding(false), 2500);
    } finally {
      setSavingOrgBranding(false);
    }
  };

  const uploadOrgLogo = async (file: File) => {
    setUploadingOrgLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("sucursalId", String(sucursales[0]?.id ?? 1));
      const res = await fetch("/api/v1/upload/logo", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (res.ok && data.url) {
        setOrgBranding(b => ({ ...b, logoUrl: data.url }));
      } else {
        alert(`Error al subir imagen: ${data.message ?? JSON.stringify(data)}`);
      }
    } catch (e: unknown) {
      alert(`Error de conexión al subir imagen: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setUploadingOrgLogo(false);
      if (orgLogoRef.current) orgLogoRef.current.value = "";
    }
  };

  const seleccionar = async (s: Sucursal) => {
    setSelected({ ...s });
    // Cargar config visual (logo, fondo, colores)
    fetch(`/api/v1/menu-publico/sucursal/${s.id}/config`)
      .then(r => r.json())
      .then(d => setMenuCfg({ logoUrl: d.logoUrl, imagenFondoUrl: d.imagenFondoUrl, colorPrimario: d.colorPrimario, colorSecundario: d.colorSecundario, mostrarLlamarMesonero: d.mostrarLlamarMesonero !== false, mostrarSolicitarCuenta: d.mostrarSolicitarCuenta !== false }))
      .catch(() => {});
    // Cargar usuarios de esta sucursal
    fetch(`/api/v1/sucursales/${s.id}/usuarios`, { headers })
      .then(r => r.json())
      .then(setSucUsers)
      .catch(() => setSucUsers([]));
    // Cargar zonas con su configuración
    fetch(`/api/v1/sucursales/${s.id}/zonas-config`, { headers })
      .then(r => r.json())
      .then(setZonasCfg)
      .catch(() => {
        // Fallback: cargar zonas básicas sin config
        fetch(`/api/v1/sucursales/${s.id}/zonas`)
          .then(r => r.json())
          .then((zs: any[]) => setZonasCfg(zs.map(z => ({ ...z, permiteSeleccionMesa: false, bloquesTiempoMinutos: null }))))
          .catch(() => setZonasCfg([]));
      });
  };

  const cargarRecursosZona = async (zonaId: number) => {
    if (recursosZona[zonaId]) return; // ya cargados
    const data = await fetch(`/api/v1/sucursales/zonas/${zonaId}/recursos`, { headers })
      .then(r => r.json()).catch(() => []);
    setRecursosZona(prev => ({ ...prev, [zonaId]: data }));
  };

  /** Copia hora inicio, hora fin y capacidad del primer recurso a todos los demás */
  const copiarPrimeraFilaATodos = (zonaId: number) => {
    const lista = recursosZona[zonaId] ?? [];
    if (lista.length < 2) return;
    const ref = lista[0];
    setRecursosZona(prev => ({
      ...prev,
      [zonaId]: prev[zonaId].map((r, i) =>
        i === 0 ? r : { ...r, horaInicio: ref.horaInicio, horaFin: ref.horaFin, capacidad: ref.capacidad }
      ),
    }));
  };

  const actualizarRecurso = (zonaId: number, recursoId: number, patch: Partial<RecursoConfig>) => {
    setRecursosZona(prev => ({
      ...prev,
      [zonaId]: (prev[zonaId] ?? []).map(r => r.id === recursoId ? { ...r, ...patch } : r),
    }));
  };

  const guardarRecursosZona = async (zonaId: number) => {
    const lista = recursosZona[zonaId] ?? [];
    await Promise.all(lista.map(r =>
      fetch(`/api/v1/sucursales/recursos/${r.id}/config`, {
        method: "PUT", headers,
        body: JSON.stringify({
          reservableOnline: r.reservableOnline,
          horaInicio: r.horaInicio || null,
          horaFin: r.horaFin || null,
          capacidad: r.capacidad,
          origen: (r as any).origen ?? "recurso",
          visibleWeb: r.visibleWeb ?? true,
          nombreWeb: r.nombreWeb ?? null,
        }),
      })
    ));
  };

  const guardarZonas = async () => {
    if (!selected) return;
    setSavingZonas(true);
    try {
      // Guardar config de zonas
      await Promise.all(zonasCfg.map(z =>
        fetch(`/api/v1/sucursales/zonas/${z.id}/config`, {
          method: "PUT", headers,
          body: JSON.stringify({
            permiteSeleccionMesa: z.permiteSeleccionMesa ?? false,
            bloquesTiempoMinutos: z.bloquesTiempoMinutos ?? null,
            politicaConfirmacionHoras: z.politicaConfirmacionHoras ?? null,
            sinVencimiento: z.sinVencimiento ?? false,
            maxDiasAnticipacion: z.maxDiasAnticipacion ?? null,
            maxReservasPeriodo: z.maxReservasPeriodo ?? null,
            periodoDias: z.periodoDias ?? 30,
            horarioSemanal: z.horarioSemanal ?? null,
            mesasMaxPorHora: z.mesasMaxPorHora ?? null,
            descripcion: z.descripcion ?? null,
            // Pago opcional
            requierePagoReserva: z.requierePagoReserva ?? false,
            montoReservaUsd: z.montoReservaUsd ?? null,
            articuloId: z.articuloId ?? null,
            instruccionesPago: z.instruccionesPago ?? null,
            horasLimitePago: z.horasLimitePago ?? 24,
            toleranciaLlegadaMinutos: z.toleranciaLlegadaMinutos ?? 30,
            aceptaWalkin: z.aceptaWalkin ?? true,
            // Visibilidad web
            visibleWeb: z.visibleWeb ?? true,
            nombreWeb: z.nombreWeb ?? null,
          }),
        })
      ));
      // Guardar recursos de zonas expandidas
      await Promise.all(Object.keys(recursosZona).map(zId => guardarRecursosZona(Number(zId))));
    } finally { setSavingZonas(false); }
  };

  const guardar = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      // Guardar config de sucursal (info general + redes + política)
      await fetch(`/api/v1/sucursales/${selected.id}/config`, {
        method: "PUT", headers,
        body: JSON.stringify({
          telefono: selected.telefono,
          email: selected.email,
          horarioApertura: selected.horarioApertura,
          whatsappNumero: selected.whatsappNumero,
          instagramUrl: selected.instagramUrl,
          facebookUrl: selected.facebookUrl,
          tripadvisorUrl: selected.tripadvisorUrl,
          websiteUrl: selected.websiteUrl,
          politicaConfirmacionHoras: selected.politicaConfirmacionHoras,
          usaDuracionReserva: selected.usaDuracionReserva,
          deliveryActivo: selected.deliveryActivo ?? true,
          deliveryHoraInicio: selected.deliveryHoraInicio || null,
          deliveryHoraFin: selected.deliveryHoraFin || null,
        }),
      });

      // Guardar info básica (nombre, dirección, ciudad)
      await fetch(`/api/v1/sucursales/${selected.id}`, {
        method: "PUT", headers,
        body: JSON.stringify({
          nombre: selected.nombre,
          codigo: selected.codigo,
          ciudad: selected.ciudad,
          provincia: selected.provincia,
          pais: selected.pais,
          direccion: selected.direccion,
          capacidadPersonas: selected.capacidadPersonas,
          activa: selected.activa,
          reservable: selected.reservable ?? true,
        }),
      });

      // Guardar logo/fondo en config de menú
      await fetch(`/api/v1/menus/config/${selected.id}`, {
        method: "PUT", headers,
        body: JSON.stringify({
          colorPrimario: menuCfg.colorPrimario,
          colorSecundario: menuCfg.colorSecundario,
          logoUrl: menuCfg.logoUrl,
          imagenFondoUrl: menuCfg.imagenFondoUrl,
          mostrarLlamarMesonero: menuCfg.mostrarLlamarMesonero !== false,
          mostrarSolicitarCuenta: menuCfg.mostrarSolicitarCuenta !== false,
        }),
      });

      setSaved(true);
      cargarSucursales();
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  const uploadImg = async (file: File, tipo: "logo" | "bg") => {
    if (!selected) return;
    setUploading(tipo);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("sucursalId", String(selected.id));
      fd.append("menuId", "0");
      const res = await fetch("/api/v1/upload/logo", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (res.ok && data.url) {
        if (tipo === "logo") setMenuCfg(c => ({ ...c, logoUrl: data.url }));
        else setMenuCfg(c => ({ ...c, imagenFondoUrl: data.url }));
      } else {
        alert(`Error al subir imagen: ${data.message ?? JSON.stringify(data)}`);
      }
    } catch (e: unknown) {
      alert(`Error de conexión al subir imagen: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setUploading(null);
      // Resetear el input para permitir subir el mismo archivo de nuevo
      if (tipo === "logo" && logoRef.current) logoRef.current.value = "";
      if (tipo === "bg" && bgRef.current) bgRef.current.value = "";
    }
  };

  const agregarUsuario = async (userId: string) => {
    if (!selected) return;
    await fetch(`/api/v1/sucursales/${selected.id}/usuarios`, {
      method: "POST", headers,
      body: JSON.stringify({ usuarioId: userId }),
    });
    // Recargar usuarios de la sucursal
    fetch(`/api/v1/sucursales/${selected.id}/usuarios`, { headers })
      .then(r => r.json()).then(setSucUsers).catch(() => {});
  };

  const quitarUsuario = async (userId: string) => {
    if (!selected) return;
    await fetch(`/api/v1/sucursales/${selected.id}/usuarios/${userId}`, {
      method: "DELETE", headers,
    });
    setSucUsers(prev => prev.filter(u => u.id !== userId));
  };

  const usersDisponibles = allUsers.filter(u => !sucUsers.some(su => su.id === u.id));

  const set = (k: keyof Sucursal, v: unknown) =>
    setSelected(s => s ? { ...s, [k]: v } : s);

  return (
    <div className="min-h-screen bg-ink-50">
      <header className="border-b border-ink-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <Link to={inicio} className="flex items-center gap-2 text-ink-500 hover:text-ink-800">
            <ArrowLeft className="size-4" /><Brand size="sm" />
          </Link>
          <h1 className="font-semibold text-ink-800">Gestión de Sucursales</h1>
          <div />
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-8 space-y-6">

        {/* Identidad del login — a nivel grupo/organización, independiente de cada sucursal */}
        <div className="card p-5">
          <h3 className="font-semibold text-ink-700 mb-1 text-sm uppercase tracking-wide">Identidad del login (grupo)</h3>
          <p className="text-xs text-ink-400 mb-4">
            Logo y color que se ven en <code>/login</code> para todo el grupo. No viene de ninguna sucursal en particular — si lo dejás vacío, el login usa el logo/color de la primera sucursal activa.
          </p>
          <div className="flex items-end gap-6 flex-wrap">
            <div>
              <label className="block text-xs text-ink-600 mb-1 font-medium">Logo</label>
              <p className="text-xs text-ink-400 mb-2">Recomendado: <strong>200×200 px</strong>, PNG/JPG, fondo transparente</p>
              <div className="flex items-center gap-3">
                {orgBranding.logoUrl
                  ? <img src={orgBranding.logoUrl} alt="Logo del grupo" className="h-14 w-14 rounded-full object-cover border-2 border-ink-200" />
                  : <div className="h-14 w-14 rounded-full bg-ink-100 border-2 border-dashed border-ink-300 flex items-center justify-center text-ink-400 text-xs text-center">Sin logo</div>
                }
                <div className="space-y-1">
                  <input ref={orgLogoRef} type="file" accept="image/*" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) uploadOrgLogo(f); }} />
                  <button onClick={() => orgLogoRef.current?.click()} disabled={uploadingOrgLogo}
                    className="btn-secondary text-xs flex items-center gap-1">
                    <Upload className="size-3" />
                    {uploadingOrgLogo ? "Subiendo..." : "Cambiar logo"}
                  </button>
                  {orgBranding.logoUrl && (
                    <button onClick={() => setOrgBranding(b => ({ ...b, logoUrl: null }))}
                      className="block text-xs text-red-400 hover:text-red-600">Quitar</button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs text-ink-600 mb-1 font-medium">Color principal</label>
              <p className="text-xs text-ink-400 mb-2">Fondo/acento del login</p>
              <div className="flex items-center gap-2">
                <input type="color" value={orgBranding.colorPrimario ?? "#6366F1"}
                  onChange={e => setOrgBranding(b => ({ ...b, colorPrimario: e.target.value }))}
                  className="h-9 w-12 rounded cursor-pointer border border-ink-200" />
                <span className="text-xs font-mono text-ink-600">{orgBranding.colorPrimario ?? "#6366F1"}</span>
              </div>
            </div>

            <button onClick={guardarOrgBranding} disabled={savingOrgBranding} className="btn-primary text-sm px-5">
              {savingOrgBranding ? "Guardando..." : savedOrgBranding ? "✓ Guardado" : "Guardar identidad del login"}
            </button>
          </div>
        </div>

        <div className="flex gap-6">

        {/* Lista de sucursales */}
        <div className="w-72 shrink-0 space-y-3">
          <h2 className="text-xs font-semibold text-ink-500 uppercase tracking-wide px-1">Sucursales</h2>
          {sucursales.map(s => (
            <button key={s.id} onClick={() => seleccionar(s)}
              className={`w-full text-left p-4 rounded-xl border transition-all ${selected?.id === s.id ? "border-brand-400 bg-brand-50 shadow-sm" : "border-ink-200 bg-white hover:border-ink-300"}`}>
              <div className="flex items-center gap-3">
                <div className={`size-9 rounded-lg flex items-center justify-center text-white font-bold text-sm ${s.activa ? "bg-brand-600" : "bg-ink-300"}`}>
                  {s.codigo?.slice(0, 2) ?? s.nombre[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-ink-800 text-sm truncate">{s.nombre}</p>
                  <p className="text-xs text-ink-400 truncate">{s.ciudad ?? "—"}</p>
                </div>
                {!s.activa && <span className="text-xs bg-ink-100 text-ink-500 px-2 py-0.5 rounded-full">Inactiva</span>}
              </div>
            </button>
          ))}
        </div>

        {/* Panel de edición */}
        {selected ? (
          <div className="flex-1 space-y-5">
            {/* Header del panel */}
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold text-ink-800">{selected.nombre}</h2>
              <div className="flex gap-2">
                <a href={`/menu/sucursal/${selected.id}`} target="_blank" rel="noreferrer"
                  className="btn-secondary text-sm flex items-center gap-2">
                  <ExternalLink className="size-4" /> Ver menú
                </a>
                <button onClick={guardar} disabled={saving}
                  className="btn-primary text-sm px-5">
                  {saving ? "Guardando..." : saved ? "✓ Guardado" : "Guardar cambios"}
                </button>
                <button
                  onClick={async () => {
                    if (!selected) return;
                    if (!window.confirm(`¿Eliminar la sucursal "${selected.nombre}"? Esta acción no se puede deshacer.`)) return;
                    try {
                      const res = await fetch(`/api/v1/sucursales/${selected.id}`, { method: "DELETE", headers });
                      if (res.status === 409) {
                        const j = await res.json().catch(() => ({} as { message?: string }));
                        window.alert(j?.message || 'No se puede eliminar: la sucursal tiene datos asociados. Márcala como "Oculta".');
                        return;
                      }
                      if (!res.ok) { window.alert("No se pudo eliminar la sucursal."); return; }
                      setSelected(null);
                      await cargarSucursales();
                    } catch { window.alert("No se pudo eliminar la sucursal."); }
                  }}
                  className="btn-secondary text-sm px-4 text-red-600">
                  Eliminar
                </button>
              </div>
            </div>

            {/* Info general */}
            <div className="card p-5">
              <h3 className="font-semibold text-ink-700 mb-4 text-sm uppercase tracking-wide">Información general</h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "Nombre *", key: "nombre" as const },
                  { label: "Código", key: "codigo" as const },
                  { label: "Ciudad", key: "ciudad" as const },
                  { label: "Provincia", key: "provincia" as const },
                  { label: "País", key: "pais" as const },
                  { label: "Capacidad (personas)", key: "capacidadPersonas" as const, type: "number" },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs text-ink-500 mb-1">{f.label}</label>
                    <input
                      type={f.type ?? "text"}
                      value={(selected[f.key] as string | number) ?? ""}
                      onChange={e => set(f.key, f.type === "number" ? Number(e.target.value) : e.target.value)}
                      className="input w-full text-sm"
                    />
                  </div>
                ))}
                <div className="col-span-2">
                  <label className="block text-xs text-ink-500 mb-1">Dirección</label>
                  <input value={selected.direccion ?? ""} onChange={e => set("direccion", e.target.value)}
                    className="input w-full text-sm" placeholder="Av. Principal #123" />
                </div>
              </div>
              <div className="flex flex-col gap-3 mt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <div onClick={() => set("activa", !selected.activa)}
                    className={`w-10 h-5 rounded-full transition-colors relative ${selected.activa ? "bg-brand-600" : "bg-ink-300"}`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${selected.activa ? "translate-x-5" : "translate-x-0"}`} />
                  </div>
                  <span className="text-sm text-ink-700">Visible (se muestra a los clientes)</span>
                </label>
                <label className={`flex items-center gap-2 ${selected.activa ? "cursor-pointer" : "opacity-40 pointer-events-none"}`}>
                  <div onClick={() => set("reservable", !(selected.reservable ?? true))}
                    className={`w-10 h-5 rounded-full transition-colors relative ${(selected.reservable ?? true) ? "bg-brand-600" : "bg-ink-300"}`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${(selected.reservable ?? true) ? "translate-x-5" : "translate-x-0"}`} />
                  </div>
                  <span className="text-sm text-ink-700">Acepta reservas online</span>
                </label>
                <p className="text-xs text-ink-500">
                  {!selected.activa
                    ? "Estado: Oculta — no aparece en el sitio de reservas."
                    : (selected.reservable ?? true)
                      ? "Estado: Activa — visible y acepta reservas."
                      : "Estado: Solo informativa — visible, pero sin botón de reservar."}
                </p>
              </div>
            </div>

            {/* Contacto */}
            <div className="card p-5">
              <h3 className="font-semibold text-ink-700 mb-4 text-sm uppercase tracking-wide">Contacto y Redes</h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "📞 Teléfono", key: "telefono" as const, placeholder: "+58 295 555-0001" },
                  { label: "📧 Email", key: "email" as const, placeholder: "francelina@email.com" },
                  { label: "🕐 Horario de atención", key: "horarioApertura" as const, placeholder: "Lun-Dom 07:00-23:00" },
                  { label: "💬 WhatsApp (solo número)", key: "whatsappNumero" as const, placeholder: "582955550001" },
                  { label: "📸 Instagram URL", key: "instagramUrl" as const, placeholder: "https://instagram.com/..." },
                  { label: "📘 Facebook URL", key: "facebookUrl" as const, placeholder: "https://facebook.com/..." },
                  { label: "🌟 TripAdvisor URL", key: "tripadvisorUrl" as const, placeholder: "https://tripadvisor.com/..." },
                  { label: "🌐 Sitio web", key: "websiteUrl" as const, placeholder: "https://francelina.com" },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs text-ink-500 mb-1">{f.label}</label>
                    <input value={(selected[f.key] as string) ?? ""}
                      onChange={e => set(f.key, e.target.value)}
                      className="input w-full text-sm" placeholder={f.placeholder} />
                  </div>
                ))}
              </div>
            </div>

            {/* Identidad visual */}
            <div className="card p-5">
              <h3 className="font-semibold text-ink-700 mb-1 text-sm uppercase tracking-wide">Identidad Visual</h3>
              <p className="text-xs text-ink-400 mb-4">Se usa en el menú digital y en la página pública de reservas.</p>

              <div className="grid grid-cols-2 gap-5">
                {/* Logo */}
                <div>
                  <label className="block text-xs text-ink-600 mb-1 font-medium">Logo</label>
                  <p className="text-xs text-ink-400 mb-2">Recomendado: <strong>200×200 px</strong>, PNG/JPG, fondo transparente</p>
                  <div className="flex items-center gap-3">
                    {menuCfg.logoUrl
                      ? <img src={menuCfg.logoUrl} alt="Logo" className="h-14 w-14 rounded-full object-cover border-2 border-ink-200" />
                      : <div className="h-14 w-14 rounded-full bg-ink-100 border-2 border-dashed border-ink-300 flex items-center justify-center text-ink-400 text-xs text-center">Sin logo</div>
                    }
                    <div className="space-y-1">
                      <input ref={logoRef} type="file" accept="image/*" className="hidden"
                        onChange={e => { const f = e.target.files?.[0]; if (f) uploadImg(f, "logo"); }} />
                      <button onClick={() => logoRef.current?.click()} disabled={!!uploading}
                        className="btn-secondary text-xs flex items-center gap-1">
                        <Upload className="size-3" />
                        {uploading === "logo" ? "Subiendo..." : "Cambiar logo"}
                      </button>
                      {menuCfg.logoUrl && (
                        <button onClick={() => setMenuCfg(c => ({ ...c, logoUrl: null }))}
                          className="block text-xs text-red-400 hover:text-red-600">Quitar</button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Imagen de fondo */}
                <div>
                  <label className="block text-xs text-ink-600 mb-1 font-medium">Imagen de fondo</label>
                  <p className="text-xs text-ink-400 mb-2">Recomendado: <strong>1920×1080 px</strong>, JPG/PNG, max 2MB</p>
                  <div className="flex items-center gap-3">
                    {menuCfg.imagenFondoUrl
                      ? <img src={menuCfg.imagenFondoUrl} alt="Fondo" className="h-14 w-20 object-cover rounded-lg border border-ink-200" />
                      : <div className="h-14 w-20 bg-ink-100 border-2 border-dashed border-ink-300 rounded-lg flex items-center justify-center text-ink-400 text-xs text-center">Sin fondo</div>
                    }
                    <div className="space-y-1">
                      <input ref={bgRef} type="file" accept="image/*" className="hidden"
                        onChange={e => { const f = e.target.files?.[0]; if (f) uploadImg(f, "bg"); }} />
                      <button onClick={() => bgRef.current?.click()} disabled={!!uploading}
                        className="btn-secondary text-xs flex items-center gap-1">
                        <Upload className="size-3" />
                        {uploading === "bg" ? "Subiendo..." : "Cambiar fondo"}
                      </button>
                      {menuCfg.imagenFondoUrl && (
                        <button onClick={() => setMenuCfg(c => ({ ...c, imagenFondoUrl: null }))}
                          className="block text-xs text-red-400 hover:text-red-600">Quitar</button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Colores */}
                <div>
                  <label className="block text-xs text-ink-600 mb-1 font-medium">Color principal</label>
                  <p className="text-xs text-ink-400 mb-2">Header del menú, botones, acentos</p>
                  <div className="flex items-center gap-2">
                    <input type="color" value={menuCfg.colorPrimario ?? "#C0392B"}
                      onChange={e => setMenuCfg(c => ({ ...c, colorPrimario: e.target.value }))}
                      className="h-9 w-12 rounded cursor-pointer border border-ink-200" />
                    <span className="text-xs font-mono text-ink-600">{menuCfg.colorPrimario ?? "#C0392B"}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-ink-600 mb-1 font-medium">Color secundario</label>
                  <p className="text-xs text-ink-400 mb-2">Botón de cuenta, acentos secundarios</p>
                  <div className="flex items-center gap-2">
                    <input type="color" value={menuCfg.colorSecundario ?? "#2980B9"}
                      onChange={e => setMenuCfg(c => ({ ...c, colorSecundario: e.target.value }))}
                      className="h-9 w-12 rounded cursor-pointer border border-ink-200" />
                    <span className="text-xs font-mono text-ink-600">{menuCfg.colorSecundario ?? "#2980B9"}</span>
                  </div>
                </div>
              </div>

              {/* Botones de notificación en el menú digital */}
              <div className="col-span-2 mt-2">
                <label className="block text-xs text-ink-600 mb-2 font-medium">Botones en el menú digital del cliente</label>
                <div className="flex gap-4 flex-wrap">
                  {[
                    { key: "mostrarLlamarMesonero" as const, label: "🔔 Llamar mesonero", desc: "El cliente puede llamar al mesonero desde su celular" },
                    { key: "mostrarSolicitarCuenta" as const, label: "📋 Pedir la cuenta", desc: "El cliente puede solicitar la cuenta desde su celular" },
                  ].map(({ key, label, desc }) => (
                    <label key={key} className="flex items-start gap-2 cursor-pointer group">
                      <div
                        onClick={() => setMenuCfg(c => ({ ...c, [key]: !(c as any)[key] !== false }))}
                        className={`mt-0.5 w-9 h-5 rounded-full transition-colors relative shrink-0 ${(menuCfg as any)[key] !== false ? "bg-brand-600" : "bg-ink-300"}`}
                      >
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${(menuCfg as any)[key] !== false ? "translate-x-4" : "translate-x-0"}`} />
                      </div>
                      <div>
                        <p className="text-xs font-medium text-ink-700">{label}</p>
                        <p className="text-[10px] text-ink-400">{desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Política de reservas */}
            <div className="card p-5">
              <h3 className="font-semibold text-ink-700 mb-4 text-sm uppercase tracking-wide">Política de reservas</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-ink-500 mb-1">Horas para confirmar (política)</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min={1} max={168}
                      value={selected.politicaConfirmacionHoras ?? 24}
                      onChange={e => set("politicaConfirmacionHoras", Number(e.target.value))}
                      className="input w-24 text-center font-bold" />
                    <span className="text-sm text-ink-500">horas antes del turno</span>
                  </div>
                  <p className="text-xs text-ink-400 mt-1">Si no se confirma antes, se cancela automáticamente.</p>
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer mt-4">
                    <div onClick={() => set("usaDuracionReserva", !selected.usaDuracionReserva)}
                      className={`w-10 h-5 rounded-full transition-colors relative ${selected.usaDuracionReserva ? "bg-brand-600" : "bg-ink-300"}`}>
                      <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${selected.usaDuracionReserva ? "translate-x-5" : "translate-x-0"}`} />
                    </div>
                    <span className="text-sm text-ink-700">Reserva por duración (padel, bowling, etc.)</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Horario de recepción de delivery */}
            <div className="card p-5">
              <h3 className="font-semibold text-ink-700 mb-1 text-sm uppercase tracking-wide flex items-center gap-2">
                <Clock className="size-4 text-brand-600" /> Horario de delivery
              </h3>
              <p className="text-xs text-ink-400 mb-4">
                Rango horario en que la sucursal acepta pedidos delivery. Los pedidos fuera de este rango se rechazan automáticamente. Es distinto al horario de atención de la tienda.
              </p>

              <label className="flex items-center gap-3 cursor-pointer mb-4">
                <div
                  onClick={() => set("deliveryActivo", !(selected.deliveryActivo ?? true))}
                  className={`w-10 h-5 rounded-full transition-colors relative ${(selected.deliveryActivo ?? true) ? "bg-brand-600" : "bg-ink-300"}`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${(selected.deliveryActivo ?? true) ? "translate-x-5" : "translate-x-0"}`} />
                </div>
                <span className="text-sm text-ink-700">
                  {(selected.deliveryActivo ?? true) ? "Delivery activo" : "Delivery desactivado (rechaza todos los pedidos)"}
                </span>
              </label>

              {(selected.deliveryActivo ?? true) && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-ink-500 mb-1">Hora inicio (recepción)</label>
                    <input
                      type="time"
                      value={selected.deliveryHoraInicio ?? ""}
                      onChange={e => set("deliveryHoraInicio", e.target.value || null)}
                      className="input w-full text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-ink-500 mb-1">Hora fin (recepción)</label>
                    <input
                      type="time"
                      value={selected.deliveryHoraFin ?? ""}
                      onChange={e => set("deliveryHoraFin", e.target.value || null)}
                      className="input w-full text-sm"
                    />
                  </div>
                  <div className="col-span-2 text-[11px] text-ink-400">
                    Deja ambos vacíos para aceptar pedidos 24/7. Si el fin es menor que el inicio (ej: 18:00 → 02:00), se interpreta como rango que cruza medianoche.
                  </div>
                </div>
              )}
            </div>

            {/* ── CONFIGURACIÓN DE ZONAS ── */}
            <div className="card p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-ink-700 text-sm uppercase tracking-wide flex items-center gap-2">
                  <LayoutGrid className="size-4" /> Zonas y selección de mesa/pista
                </h3>
                <button onClick={guardarZonas} disabled={savingZonas}
                  className="btn-primary text-xs px-3 py-1.5">
                  {savingZonas ? "..." : "Guardar zonas"}
                </button>
              </div>
              <p className="text-xs text-ink-400 mb-4">
                Activa "Permite elegir" para que el cliente seleccione una pista, mesa o recurso específico al reservar desde la web.
                Con "Tiempo" defines los bloques de duración disponibles.
              </p>

              {zonasCfg.length === 0 ? (
                <p className="text-sm text-ink-400 py-2">Sin zonas configuradas. Sincroniza el ICG primero.</p>
              ) : (
                <div className="space-y-2">
                  {/* Cabecera */}
                  <div className="grid grid-cols-12 gap-2 px-2 text-xs font-semibold text-ink-500 uppercase tracking-wide">
                    <div className="col-span-3">Zona</div>
                    <div className="col-span-3 text-center">¿Permite elegir mesa?</div>
                    <div className="col-span-3 text-center">Tiempo de bloque</div>
                    <div className="col-span-3 text-center">Política (h)</div>
                  </div>
                  {zonasCfg.map(z => {
                    const recursos = recursosZona[z.id] ?? [];
                    const expandida = zonaExpandida === z.id;
                    return (
                      <div key={z.id} className="border border-ink-200 rounded-xl overflow-hidden">
                        {/* Fila de zona */}
                        <div className="grid grid-cols-12 gap-2 items-center px-3 py-2.5 bg-ink-50">
                          {/* Nombre + expandir */}
                          <button
                            className="col-span-3 flex items-center gap-2 text-left"
                            onClick={async () => {
                              const nuevaExpandida = expandida ? null : z.id;
                              setZonaExpandida(nuevaExpandida);
                              if (nuevaExpandida) await cargarRecursosZona(z.id);
                            }}
                          >
                            <ChevronRight className={`size-4 text-ink-400 transition-transform ${expandida ? "rotate-90" : ""}`} />
                            <p className="text-sm font-medium text-ink-800">{z.nombre}</p>
                            <span className="text-xs text-ink-400">({recursos.length || "..."} mesas)</span>
                          </button>
                          {/* Toggle permite selección online */}
                          <div className="col-span-3 flex justify-center">
                            <button
                              onClick={() => setZonasCfg(prev => prev.map(x =>
                                x.id === z.id ? { ...x, permiteSeleccionMesa: !x.permiteSeleccionMesa } : x
                              ))}
                              className="flex items-center gap-2"
                            >
                              {z.permiteSeleccionMesa
                                ? <CheckSquare className="size-5 text-brand-600" />
                                : <Square className="size-5 text-ink-300" />}
                              <span className={`text-xs font-medium ${z.permiteSeleccionMesa ? "text-brand-700" : "text-ink-400"}`}>
                                {z.permiteSeleccionMesa ? "Sí" : "No"}
                              </span>
                            </button>
                          </div>
                          {/* Bloque de tiempo */}
                          <div className="col-span-3 flex justify-center">
                            {z.permiteSeleccionMesa ? (
                              <div className="flex items-center gap-1">
                                <Clock className="size-3.5 text-ink-400" />
                                <select
                                  value={z.bloquesTiempoMinutos ?? ""}
                                  onChange={e => setZonasCfg(prev => prev.map(x =>
                                    x.id === z.id ? { ...x, bloquesTiempoMinutos: e.target.value ? Number(e.target.value) : null } : x
                                  ))}
                                  className="text-xs border border-ink-200 rounded px-1.5 py-1"
                                >
                                  <option value="">Por turno</option>
                                  <option value="30">30 min</option>
                                  <option value="60">1 hora</option>
                                  <option value="90">1.5 horas</option>
                                  <option value="120">2 horas</option>
                                  <option value="180">3 horas</option>
                                </select>
                              </div>
                            ) : (
                              <span className="text-xs text-ink-300">—</span>
                            )}
                          </div>
                          {/* Política de confirmación (horas) */}
                          <div className="col-span-3 flex justify-center">
                            <input
                              type="number"
                              min={1}
                              max={168}
                              placeholder="Global"
                              value={z.politicaConfirmacionHoras ?? ""}
                              onChange={e => setZonasCfg(prev => prev.map(x =>
                                x.id === z.id ? { ...x, politicaConfirmacionHoras: e.target.value ? Number(e.target.value) : null } : x
                              ))}
                              className="text-xs border border-ink-200 rounded px-1.5 py-1 w-20 text-center"
                              title="Horas para confirmar (sobrescribe la política de la sucursal)"
                            />
                          </div>
                        </div>

                        {/* Tolerancia de llegada (no-show de reserva ya confirmada) */}
                        <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-t border-ink-100 bg-white">
                          <div className="col-span-3 text-xs font-medium text-ink-600">Tolerancia de llegada</div>
                          <div className="col-span-3 flex items-center gap-2">
                            <input
                              type="number"
                              min={5}
                              max={240}
                              value={z.toleranciaLlegadaMinutos ?? 30}
                              onChange={e => setZonasCfg(prev => prev.map(x =>
                                x.id === z.id ? { ...x, toleranciaLlegadaMinutos: e.target.value ? Number(e.target.value) : null } : x
                              ))}
                              className="text-xs border border-ink-200 rounded px-1.5 py-1 w-20 text-center"
                            />
                            <span className="text-xs text-ink-500">minutos</span>
                          </div>
                          <div className="col-span-6 text-[11px] text-ink-400">
                            Si ya confirmó pero no llega (sin check-in) dentro de este tiempo después de su hora, la reserva se cancela sola y pasa al siguiente de la lista de espera.
                          </div>
                        </div>

                        {/* Acepta walk-in (fila del QR de la puerta) */}
                        <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-t border-ink-100 bg-white">
                          <div className="col-span-3 flex items-center gap-2">
                            <button
                              onClick={() => setZonasCfg(prev => prev.map(x =>
                                x.id === z.id ? { ...x, aceptaWalkin: !(x.aceptaWalkin ?? true) } : x
                              ))}
                              className="flex items-center gap-1.5"
                              title="Si está desactivado, un cupo liberado de esta zona nunca se le ofrece a la fila walk-in de la puerta"
                            >
                              {(z.aceptaWalkin ?? true)
                                ? <CheckSquare className="size-4 text-brand-600" />
                                : <Square className="size-4 text-ink-300" />}
                              <span className={`text-xs font-medium ${(z.aceptaWalkin ?? true) ? "text-brand-700" : "text-ink-400"}`}>
                                Acepta walk-in
                              </span>
                            </button>
                          </div>
                          <div className="col-span-9 text-[11px] text-ink-400">
                            Si está desactivado (ej. zonas privadas/VIP), un cupo que se libera de esta zona por no-show nunca se le ofrece a quien está esperando en la fila walk-in de la puerta.
                          </div>
                        </div>

                        {/* Visibilidad web + nombre alternativo */}
                        <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-t border-ink-100 bg-white">
                          <div className="col-span-3 flex items-center gap-2">
                            <button
                              onClick={() => setZonasCfg(prev => prev.map(x =>
                                x.id === z.id ? { ...x, visibleWeb: !(x.visibleWeb ?? true) } : x
                              ))}
                              className="flex items-center gap-1.5"
                              title="Si está desactivado, esta zona no aparece en la web pública de reservas"
                            >
                              {(z.visibleWeb ?? true)
                                ? <CheckSquare className="size-4 text-brand-600" />
                                : <Square className="size-4 text-ink-300" />}
                              <span className={`text-xs font-medium ${(z.visibleWeb ?? true) ? "text-brand-700" : "text-ink-400"}`}>
                                Visible en web
                              </span>
                            </button>
                          </div>
                          <div className="col-span-9 flex items-center gap-2">
                            <span className="text-xs text-ink-500 whitespace-nowrap">Nombre web:</span>
                            <input
                              type="text"
                              placeholder={z.nombre}
                              value={z.nombreWeb ?? ""}
                              onChange={e => setZonasCfg(prev => prev.map(x =>
                                x.id === z.id ? { ...x, nombreWeb: e.target.value || null } : x
                              ))}
                              className="text-xs border border-ink-200 rounded px-2 py-1 flex-1"
                              title="Nombre alternativo que ve el cliente en la web (vacío = usar el mismo nombre)"
                            />
                          </div>
                        </div>

                        {/* Reglas de reserva: anticipación + límite por cliente */}
                        <div className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-t border-ink-100 bg-white">
                          <div className="col-span-4 text-xs text-ink-500 flex items-center gap-1">
                            <span>📅</span>
                            <span>Anticipación máxima (días)</span>
                          </div>
                          <div className="col-span-3 flex items-center gap-1.5">
                            <input
                              type="number" min={1} max={365}
                              placeholder="∞ sin límite"
                              value={z.maxDiasAnticipacion ?? ""}
                              onChange={e => setZonasCfg(prev => prev.map(x =>
                                x.id === z.id ? { ...x, maxDiasAnticipacion: e.target.value ? Number(e.target.value) : null } : x
                              ))}
                              className="text-xs border border-ink-200 rounded px-1.5 py-1 w-24 text-center"
                              title="Máximo de días al futuro que el cliente puede reservar (vacío = sin límite)"
                            />
                            <span className="text-[10px] text-ink-400">días</span>
                          </div>
                          <div className="col-span-2 text-xs text-ink-500 text-right">Máx. reservas/cliente</div>
                          <div className="col-span-3 flex items-center gap-1.5">
                            <input
                              type="number" min={1} max={99}
                              placeholder="∞"
                              value={z.maxReservasPeriodo ?? ""}
                              onChange={e => setZonasCfg(prev => prev.map(x =>
                                x.id === z.id ? { ...x, maxReservasPeriodo: e.target.value ? Number(e.target.value) : null } : x
                              ))}
                              className="text-xs border border-ink-200 rounded px-1.5 py-1 w-16 text-center"
                              title="Máximo de reservas por cliente (vacío = sin límite)"
                            />
                            <span className="text-[10px] text-ink-400">en</span>
                            <input
                              type="number" min={1} max={365}
                              value={z.periodoDias ?? 30}
                              onChange={e => setZonasCfg(prev => prev.map(x =>
                                x.id === z.id ? { ...x, periodoDias: Number(e.target.value) || 30 } : x
                              ))}
                              className="text-xs border border-ink-200 rounded px-1.5 py-1 w-14 text-center"
                              title="Período en días para contar las reservas del cliente"
                            />
                            <span className="text-[10px] text-ink-400">días</span>
                          </div>
                        </div>

                        {/* Cap de mesas reservables por hora (con lista de espera FIFO) */}
                        <div className="px-3 py-3 border-t-2 border-amber-200 bg-gradient-to-r from-amber-50 to-amber-50/40">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-base">🪑</span>
                            <span className="text-sm font-bold text-amber-900">
                              Capacidad reservable por hora
                            </span>
                            <span className="ml-auto text-[10px] uppercase tracking-wider text-amber-700 font-semibold bg-amber-100 px-2 py-0.5 rounded-full border border-amber-200">
                              cap + lista de espera
                            </span>
                          </div>
                          <div className="grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-12 sm:col-span-5 flex items-center gap-2">
                              <input
                                type="number" min={1} max={500}
                                placeholder="sin límite"
                                value={z.mesasMaxPorHora ?? ""}
                                onChange={e => setZonasCfg(prev => prev.map(x =>
                                  x.id === z.id ? { ...x, mesasMaxPorHora: e.target.value ? Number(e.target.value) : null } : x
                                ))}
                                className="text-base font-bold border-2 border-amber-300 rounded-lg px-3 py-2 w-32 text-center bg-white focus:border-amber-500 focus:outline-none"
                                title="Si tu terraza tiene 10 mesas y solo querés que se puedan reservar 5, poné 5."
                              />
                              <div className="text-xs text-ink-600">
                                <div className="font-semibold">mesas / hora</div>
                                <div className="text-[10px] text-ink-500">vacío = sin límite</div>
                              </div>
                            </div>
                            <div className="col-span-12 sm:col-span-7 text-xs text-ink-600 leading-relaxed">
                              <p>
                                <b className="text-amber-900">Ej:</b> si tu terraza tiene 10 mesas y ponés <b>5</b>, solo se aceptarán 5 reservas por hora. Las que superen el cupo entran a <b>lista de espera</b> y se notifica automáticamente al cliente cuando se libera un cupo.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Pago opcional para reservar — sólo si el módulo global está activo */}
                        {!moduloPagosActivo ? (
                          <div className="px-3 py-3 border-t-2 border-ink-200 bg-ink-50/40">
                            <p className="text-[12px] text-ink-600 leading-relaxed">
                              💵 <b>Pago para reservar — módulo desactivado.</b> Para usarlo activá el módulo en{" "}
                              <Link to="/tienda/pagos-reservas" className="text-emerald-700 underline font-semibold">
                                Tienda → Pagos de reservas
                              </Link>.
                            </p>
                          </div>
                        ) : (
                        <div className="px-3 py-3 border-t-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-emerald-50/40">
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!z.requierePagoReserva}
                              onChange={(e) => setZonasCfg(prev => prev.map(x =>
                                x.id === z.id ? { ...x, requierePagoReserva: e.target.checked } : x
                              ))}
                              className="size-4 accent-emerald-600 mt-0.5"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-base">💵</span>
                                <span className="text-sm font-bold text-emerald-900">
                                  Requiere pago para reservar
                                </span>
                                <span className="ml-auto text-[10px] uppercase tracking-wider text-emerald-700 font-semibold bg-emerald-100 px-2 py-0.5 rounded-full border border-emerald-200">
                                  opcional
                                </span>
                              </div>
                              <p className="text-[11px] text-ink-600 mt-1 leading-relaxed">
                                Útil para zonas que se reservan <b>por hora</b> (pistas de bowling, mesas privadas, espacios cerrados).
                                El cliente debe reportar el pago y vos lo aprobás para confirmar la reserva.
                              </p>
                            </div>
                          </label>

                          {/* Inputs solo si está activo */}
                          {z.requierePagoReserva && (
                            <div className="mt-3 pl-6 space-y-3 animate-fade-in">
                              <div className="grid grid-cols-12 gap-2 items-center">
                                <div className="col-span-12 sm:col-span-3 text-xs font-semibold text-emerald-900">
                                  💲 Monto en USD
                                </div>
                                <div className="col-span-12 sm:col-span-4 flex items-center gap-2">
                                  <span className="text-sm text-ink-600">$</span>
                                  <input
                                    type="number" min={0} step={0.01}
                                    placeholder="20.00"
                                    value={z.montoReservaUsd ?? ""}
                                    onChange={(e) => setZonasCfg(prev => prev.map(x =>
                                      x.id === z.id ? { ...x, montoReservaUsd: e.target.value ? Number(e.target.value) : null } : x
                                    ))}
                                    className="text-base font-bold border-2 border-emerald-300 rounded-lg px-3 py-2 w-32 text-center bg-white focus:border-emerald-500 focus:outline-none"
                                  />
                                  <span className="text-xs text-ink-500">USD</span>
                                </div>
                                <div className="col-span-12 sm:col-span-5 text-[11px] text-ink-500">
                                  Lo que el cliente debe pagar para asegurar su reserva.
                                </div>
                              </div>

                              <div className="grid grid-cols-12 gap-2 items-start">
                                <div className="col-span-12 sm:col-span-3 text-xs font-semibold text-emerald-900 pt-2">
                                  Instrucciones
                                </div>
                                <div className="col-span-12 sm:col-span-9">
                                  <textarea
                                    placeholder="Ej: Pago Móvil al 0414-1234567 (Banesco) · Zelle: pagos@robusta.com · Transferencia BBVA 0123-..."
                                    value={z.instruccionesPago ?? ""}
                                    onChange={(e) => setZonasCfg(prev => prev.map(x =>
                                      x.id === z.id ? { ...x, instruccionesPago: e.target.value } : x
                                    ))}
                                    rows={3}
                                    className="w-full text-xs border-2 border-emerald-200 rounded-lg px-3 py-2 bg-white focus:border-emerald-500 focus:outline-none"
                                  />
                                  <p className="text-[10px] text-ink-500 mt-1">
                                    Estas instrucciones se mostrarán al cliente después de hacer la reserva.
                                  </p>
                                </div>
                              </div>

                              <div className="grid grid-cols-12 gap-2 items-start">
                                <div className="col-span-12 sm:col-span-3 text-xs font-semibold text-emerald-900 pt-2">
                                  Política de reserva
                                </div>
                                <div className="col-span-12 sm:col-span-9">
                                  <textarea
                                    placeholder="Ej: El monto de la reserva no es reembolsable. Se requiere pago previo para garantizar su reservación..."
                                    value={z.mensajePoliticaReserva ?? ""}
                                    onChange={(e) => setZonasCfg(prev => prev.map(x =>
                                      x.id === z.id ? { ...x, mensajePoliticaReserva: e.target.value } : x
                                    ))}
                                    rows={3}
                                    className="w-full text-xs border-2 border-emerald-200 rounded-lg px-3 py-2 bg-white focus:border-emerald-500 focus:outline-none"
                                  />
                                  <p className="text-[10px] text-ink-500 mt-1">
                                    Este mensaje se muestra al cliente <b>antes</b> de enviar la reserva, junto al monto requerido.
                                  </p>
                                </div>
                              </div>

                              {/* Plazo en horas para reportar el pago */}
                              <div className="grid grid-cols-12 gap-2 items-center">
                                <div className="col-span-12 sm:col-span-3 text-xs font-semibold text-emerald-900">
                                  Plazo para pagar
                                </div>
                                <div className="col-span-12 sm:col-span-4 flex items-center gap-2">
                                  <input
                                    type="number" min={1} max={168} step={1}
                                    placeholder="24"
                                    value={z.horasLimitePago ?? 24}
                                    onChange={(e) => setZonasCfg(prev => prev.map(x =>
                                      x.id === z.id ? { ...x, horasLimitePago: e.target.value ? Number(e.target.value) : null } : x
                                    ))}
                                    className="text-base font-bold border-2 border-emerald-300 rounded-lg px-3 py-2 w-24 text-center bg-white focus:border-emerald-500 focus:outline-none"
                                  />
                                  <span className="text-xs text-ink-600">horas</span>
                                </div>
                                <div className="col-span-12 sm:col-span-5 text-[11px] text-ink-500 leading-relaxed">
                                  Si el cliente no reporta el pago en este tiempo, la reserva se
                                  <b> cancela automáticamente</b> y le llega un email avisando.
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        )}

                        {/* Horario semanal — visible cuando la zona está expandida */}
                        {expandida && (
                          <div className="border-t border-ink-100 bg-white px-3 py-3">
                            <HorarioSemanalEditor
                              zonaId={z.id}
                              horario={z.horarioSemanal}
                              onChange={(h) => setZonasCfg(prev => prev.map(x =>
                                x.id === z.id ? { ...x, horarioSemanal: h } : x
                              ))}
                            />
                          </div>
                        )}

                        {/* Recursos expandidos */}
                        {expandida && (
                          <div className="border-t border-ink-200 bg-white">
                            {/* Tabla simplificada: solo Online + Capacidad */}
                            {/* El horario lo gestiona el Horario semanal de la zona */}
                            <div className="grid grid-cols-12 gap-1 px-4 py-1.5 bg-ink-50 text-[10px] font-semibold text-ink-400 uppercase tracking-wide items-center">
                              <div className="col-span-3">Mesa / Pista</div>
                              <div className="col-span-1 text-center">Web</div>
                              <div className="col-span-3">Nombre web</div>
                              <div className="col-span-2 text-center">¿Online?</div>
                              <div className="col-span-3 text-center flex flex-col items-center gap-0.5">
                                <span>Cap. máx.</span>
                                {(recursosZona[z.id] ?? []).length > 1 && (
                                  <button
                                    onClick={() => {
                                      const ref = recursosZona[z.id]?.[0];
                                      if (!ref) return;
                                      setRecursosZona(prev => ({
                                        ...prev,
                                        [z.id]: prev[z.id].map((r, i) => i === 0 ? r : { ...r, capacidad: ref.capacidad })
                                      }));
                                    }}
                                    title="Copiar capacidad de la primera fila a todas"
                                    className="text-[9px] text-brand-600 hover:text-brand-800 font-bold leading-none"
                                  >↓ todas</button>
                                )}
                              </div>
                            </div>
                            {recursos.length === 0 ? (
                              <p className="px-4 py-3 text-xs text-ink-400">Sin mesas/pistas configuradas para esta zona.</p>
                            ) : recursos.map(r => (
                              <div key={r.id} className="grid grid-cols-12 gap-1 items-center px-4 py-2 border-t border-ink-50">
                                <div className="col-span-3">
                                  <p className="text-xs font-medium text-ink-800">{r.nombre}</p>
                                  {r.tipoNombre && <p className="text-[10px] text-ink-400">{r.tipoNombre}</p>}
                                </div>
                                {/* Visible en web */}
                                <div className="col-span-1 flex justify-center">
                                  <button onClick={() => actualizarRecurso(z.id, r.id, { visibleWeb: !(r.visibleWeb ?? true) })}
                                    title={(r.visibleWeb ?? true) ? "Visible en web — clic para ocultar" : "Oculta en web — clic para mostrar"}>
                                    {(r.visibleWeb ?? true)
                                      ? <CheckSquare className="size-4 text-emerald-600" />
                                      : <Square className="size-4 text-ink-300" />}
                                  </button>
                                </div>
                                {/* Nombre web */}
                                <div className="col-span-3">
                                  <input
                                    type="text"
                                    placeholder={r.nombre}
                                    value={r.nombreWeb ?? ""}
                                    onChange={e => actualizarRecurso(z.id, r.id, { nombreWeb: e.target.value || null })}
                                    className="text-xs border border-ink-200 rounded px-1.5 py-1 w-full"
                                    title="Nombre alternativo para la web (vacío = usar el nombre original)"
                                  />
                                </div>
                                {/* Reservable online */}
                                <div className="col-span-2 flex justify-center">
                                  <button onClick={() => actualizarRecurso(z.id, r.id, { reservableOnline: !r.reservableOnline })}
                                    title={r.reservableOnline ? "Activo online — clic para desactivar" : "Inactivo online — clic para activar"}>
                                    {r.reservableOnline
                                      ? <CheckSquare className="size-4 text-brand-600" />
                                      : <Square className="size-4 text-ink-300" />}
                                  </button>
                                </div>
                                {/* Capacidad */}
                                <div className="col-span-3 flex justify-center">
                                  <input
                                    type="number" min={1}
                                    value={r.capacidad || ""}
                                    onChange={e => actualizarRecurso(z.id, r.id, { capacidad: Number(e.target.value) || 0 })}
                                    placeholder="—"
                                    className="text-xs border border-ink-200 rounded px-1 py-1 w-16 text-center"
                                  />
                                </div>
                              </div>
                            ))}
                            {recursos.length > 0 && (
                              <div className="px-4 py-2 bg-ink-50 border-t border-ink-100">
                                <p className="text-xs text-ink-400">
                                  💡 El horario de disponibilidad se configura en "Horario por día" de la zona (arriba). Aquí solo se define si la pista/mesa acepta reservas online y su capacidad.
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Usuarios de esta sucursal */}
            <div className="card p-5">
              <h3 className="font-semibold text-ink-700 mb-1 text-sm uppercase tracking-wide flex items-center gap-2">
                <Users className="size-4" /> Usuarios con acceso
              </h3>
              <p className="text-xs text-ink-400 mb-4">Los administradores tienen acceso a todas las sucursales. Aquí configuras acceso para supervisores y recepción.</p>

              {/* Usuarios actuales */}
              <div className="space-y-2 mb-4">
                {sucUsers.length === 0 ? (
                  <p className="text-sm text-ink-400 py-2">Sin usuarios específicos asignados (solo admins tienen acceso).</p>
                ) : (
                  sucUsers.map(u => (
                    <div key={u.id} className="flex items-center justify-between px-3 py-2 bg-ink-50 rounded-lg">
                      <div>
                        <span className="text-sm font-medium text-ink-800">{u.fullName}</span>
                        <span className="text-xs text-ink-400 ml-2">{u.email}</span>
                      </div>
                      <button onClick={() => quitarUsuario(u.id)}
                        className="p-1 text-ink-400 hover:text-red-500 transition-colors">
                        <X className="size-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              {/* Agregar usuario */}
              {usersDisponibles.length > 0 && (
                <div>
                  <p className="text-xs text-ink-500 mb-2 font-medium">Agregar usuario:</p>
                  <div className="flex flex-wrap gap-2">
                    {usersDisponibles.filter(u => u.role !== "admin").map(u => (
                      <button key={u.id} onClick={() => agregarUsuario(u.id)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-white border border-ink-200 hover:border-brand-400 text-sm rounded-lg transition-colors">
                        <Plus className="size-3 text-brand-600" />
                        <span className="text-ink-700">{u.fullName}</span>
                        <span className="text-xs text-ink-400">({u.role})</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-ink-400">
            <div className="text-center">
              <Building2 className="size-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Selecciona una sucursal para editar</p>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
